import React, { useEffect, useRef, useState } from 'react';
import { App, Form, Input, Modal } from 'antd';
import { getQrConfig, validateQR } from '../../api/qr';
import { getApiErrorMessage } from '../../utils/apiError';

const CameraIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);

export default function QRValidate() {
  const { message, notification } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [signatureRequired, setSignatureRequired] = useState(true);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const onSubmit = async () => {
    try {
      const v = await form.validateFields();
      setLoading(true);
      if (!signatureRequired && !String(v.signature || '').trim()) {
        notification.info({ message: 'Mode QR sans signature — signature optionnelle.', placement: 'topRight' });
      }
      await validateQR({ token: v.token, signature: v.signature, sortie_id: Number(v.sortie_id) });
      message.success('QR validé : action enregistrée avec succès.');
      form.resetFields();
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Validation du QR échouée. Réessayez.'), placement: 'topRight' });
    } finally { setLoading(false); }
  };

  const stopStream = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  useEffect(() => () => stopStream(), []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try { const cfg = await getQrConfig(); if (mounted) setSignatureRequired(Boolean(cfg?.signature_required)); }
      catch { if (mounted) setSignatureRequired(true); }
    })();
    return () => { mounted = false; };
  }, []);

  const fillFormFromPayload = (txt) => {
    try {
      const obj = JSON.parse(txt);
      const token = obj.token || obj.tok || obj.TOKEN;
      const signature = obj.signature || obj.sig || obj.SIGNATURE;
      const sortie_id = obj.sortie_id || obj.sid || obj.SORTIE_ID;
      if (token && sortie_id && (signature || !signatureRequired)) {
        form.setFieldsValue({ token, signature: signature || undefined, sortie_id });
        message.success('QR détecté'); setScanOpen(false); stopStream();
      } else { notification.warning({ message: 'QR détecté mais format inattendu', placement: 'topRight' }); }
    } catch { /* not JSON */ }
  };

  const ensureJsQRLoaded = () => new Promise((resolve, reject) => {
    if (window.jsQR) return resolve();
    const s = document.createElement('script'); s.src = 'https://unpkg.com/jsqr/dist/jsQR.js'; s.async = true;
    s.onload = () => resolve(); s.onerror = () => reject(new Error('jsQR load failed'));
    document.body.appendChild(s);
  });

  const startScan = async () => {
    try {
      setScanOpen(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        const loop = async () => { try { const bitmaps = await detector.detect(videoRef.current); if (bitmaps?.length) { const raw = bitmaps[0].rawValue || bitmaps[0].rawValueText || ''; if (raw) { fillFormFromPayload(raw); return; } } } catch {} rafRef.current = requestAnimationFrame(loop); };
        loop();
      } else {
        await ensureJsQRLoaded();
        const ctx = canvasRef.current.getContext('2d');
        const loop = () => { try { const w = videoRef.current.videoWidth; const h = videoRef.current.videoHeight; if (w && h) { canvasRef.current.width = w; canvasRef.current.height = h; ctx.drawImage(videoRef.current, 0, 0, w, h); const img = ctx.getImageData(0, 0, w, h); const result = window.jsQR(img.data, w, h); if (result?.data) { fillFormFromPayload(result.data); return; } } } catch {} rafRef.current = requestAnimationFrame(loop); };
        loop();
      }
    } catch { notification.error({ message: 'Accès caméra refusé ou indisponible', placement: 'topRight' }); setScanOpen(false); stopStream(); }
  };

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 480 }}>
        <div style={{ padding: '14px 16px', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Valider un QR étudiant</div>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>Entrez le token, la signature et l'ID de la sortie affichés par l'étudiant.</p>
        </div>
        <div className="card-inner">
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Form.Item name="token" label="Token" rules={[{ required: true }]} style={{ marginBottom: 12 }}><Input /></Form.Item>
            <Form.Item name="signature" label="Signature" rules={signatureRequired ? [{ required: true }] : []} style={{ marginBottom: 12 }}><Input /></Form.Item>
            <Form.Item name="sortie_id" label="ID de la sortie" rules={[{ required: true }]} style={{ marginBottom: 16 }}><Input type="number" /></Form.Item>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" style={{ flex: 1 }} type="button" onClick={onSubmit} disabled={loading}>{loading ? 'Validation…' : 'Valider le QR'}</button>
              <button className="btn-outline" type="button" onClick={startScan} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><CameraIcon /> Caméra</button>
            </div>
          </Form>
        </div>
      </div>

      <Modal title="Scanner un QR" open={scanOpen} onCancel={() => { setScanOpen(false); stopStream(); }} footer={<button className="btn-outline" onClick={() => { setScanOpen(false); stopStream(); }}>Fermer</button>} destroyOnClose>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <video ref={videoRef} style={{ width: '100%', maxWidth: 420, borderRadius: 8 }} muted playsInline />
        </div>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <p style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Placez le QR dans le cadre. Le formulaire se remplira automatiquement.</p>
      </Modal>
    </div>
  );
}
